import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { 
  Crown, 
  CheckCircle, 
  ExternalLink, 
  CreditCard, 
  Calendar,
  Zap,
  Users,
  ChefHat,
  Download
} from 'lucide-react';
import { DISPLAY_NAME, normalizeTier, isAIEnabled } from '../../../billing/tiers';
import type { SubscriptionTier } from '../../../types/subscription';

interface UsageData {
  mealPlans: { current: number; limit: number };
  recipeSwaps: { current: number; limit: number };
  aiGenerations: { current: number; limit: number };
}

interface PlanFeature {
  name: string;
  included: boolean;
}

interface BillingItem {
  id: string;
  date: string;
  amount: string;
  status: 'paid' | 'pending' | 'failed';
  plan: string;
  invoice?: string;
}

interface SubscriptionSectionProps {
  subscription: {
    tier: string;
    planName: string;
    price: number;
    billingPeriod: 'monthly' | 'yearly';
    status: 'active' | 'expired' | 'trialing';
    nextBillingDate?: string;
    features: PlanFeature[];
  };
  usage: UsageData;
  billingHistory: BillingItem[];
  onUpgrade: () => void;
  onManageBilling: () => void;
  onDownloadInvoice: (invoiceId: string) => void;
}

const UsageBar = ({ label, current, limit }: { 
  label: string; 
  current: number; 
  limit: number 
}) => {
  const percentage = limit === -1 ? 100 : Math.min((current / limit) * 100, 100);
  const isOverLimit = current > limit && limit !== -1;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-dusk">{label}</span>
        <span className={`font-semibold ${isOverLimit ? 'text-red-600' : 'text-supporting'}`}>
          {current} / {limit === -1 ? '∞' : limit}
        </span>
      </div>
      <Progress 
        value={percentage} 
        className={`h-2 ${isOverLimit ? 'bg-red-100' : 'bg-stone/20'}`}
      />
    </div>
  );
};

const PlanIcon = ({ tier }: { tier: string }) => {
  const getIconAndColor = () => {
    const normalized = normalizeTier(tier);
    switch (normalized) {
      case 'core_plus':
        return { icon: Crown, color: 'text-warning' };
      case 'starter':
        return { icon: Users, color: 'text-warning' };
      default:
        return { icon: ChefHat, color: 'text-supporting' };
    }
  };

  const { icon: Icon, color } = getIconAndColor();
  return <Icon className={`w-6 h-6 ${color}`} />;
};

const BillingHistoryTable = ({ 
  history, 
  onDownloadInvoice 
}: { 
  history: BillingItem[];
  onDownloadInvoice: (invoiceId: string) => void;
}) => (
  <Card className="border border-stone">
    <CardHeader className="flex flex-row items-center justify-between py-4">
      <h3 className="text-lg font-semibold text-dusk font-header">Billing History</h3>
      <Button
        variant="outline"
        size="sm"
        className="border-stone hover:bg-stone/10"
      >
        <Download className="w-4 h-4 mr-2" />
        Download All
      </Button>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        {history.map((item) => (
          <div key={item.id} className="flex items-center justify-between py-3 border-b border-stone/20 last:border-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-coral/10 rounded-full flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-warning" />
              </div>
              <div>
                <div className="font-medium text-dusk">{item.plan} Plan</div>
                <div className="text-sm text-supporting flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  {item.date}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="font-semibold text-dusk">{item.amount}</div>
                <Badge 
                  variant="outline"
                  className={
                    item.status === 'paid' 
                      ? 'text-sage border-sage/20 bg-sage/10'
                      : item.status === 'pending'
                      ? 'text-coral-600 border-coral/20 bg-coral/10'
                      : 'text-red-600 border-red/20 bg-red/10'
                  }
                >
                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                </Badge>
              </div>
              {item.invoice && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDownloadInvoice(item.invoice!)}
                  className="p-2"
                >
                  <Download className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export function SubscriptionSection({ 
  subscription, 
  usage, 
  billingHistory, 
  onUpgrade, 
  onManageBilling,
  onDownloadInvoice 
}: SubscriptionSectionProps) {
  const normalizedTier = normalizeTier(subscription.tier);
  const isFreeTier = normalizedTier === 'demo' || subscription.price === 0;
  const isPremiumTier = isAIEnabled(normalizedTier);

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <Card className="border border-stone bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-dusk font-header">Current Plan</h3>
            {isFreeTier && (
              <Button
                onClick={onUpgrade}
                className="bg-coral hover:bg-coral/90 text-ink font-medium"
              >
                <Zap className="w-4 h-4 mr-2" />
                Upgrade Now
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Plan Details */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <PlanIcon tier={normalizedTier} />
                </div>
                <div>
                  <h4 className="font-semibold text-dusk font-header">{DISPLAY_NAME[normalizedTier]} Plan</h4>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold text-dusk">
                      ${subscription.price}
                    </p>
                    {subscription.price > 0 && (
                      <span className="text-sm text-supporting">
                        /{subscription.billingPeriod === 'yearly' ? 'year' : 'month'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Features */}
              <div className="space-y-2">
                <h5 className="font-medium text-dusk text-sm">What's included:</h5>
                {subscription.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-sage flex-shrink-0" />
                    <span className="text-sm text-supporting">{feature.name}</span>
                  </div>
                ))}
              </div>

              {/* Next billing */}
              {subscription.nextBillingDate && (
                <div className="mt-4 p-3 bg-stone/10 rounded-lg">
                  <p className="text-sm text-supporting">
                    Next billing: {subscription.nextBillingDate}
                  </p>
                </div>
              )}
            </div>
            
            {/* Usage Statistics */}
            <div>
              <h5 className="font-medium text-dusk mb-3">Usage This Month</h5>
              <div className="space-y-4">
                <UsageBar 
                  label="Meal Plans"
                  current={usage.mealPlans.current}
                  limit={usage.mealPlans.limit}
                />
                <UsageBar 
                  label="Recipe Swaps"
                  current={usage.recipeSwaps.current}
                  limit={usage.recipeSwaps.limit}
                />
                <UsageBar 
                  label="AI Generations"
                  current={usage.aiGenerations.current}
                  limit={usage.aiGenerations.limit}
                />
              </div>
              
              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-2 mt-6">
                {!isPremiumTier && (
                  <Button
                    onClick={onUpgrade}
                    className="bg-coral hover:bg-coral/90 text-ink font-medium"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={onManageBilling}
                  className="border-stone hover:bg-stone/10"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Manage Billing
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Billing History */}
      <BillingHistoryTable 
        history={billingHistory} 
        onDownloadInvoice={onDownloadInvoice}
      />
    </div>
  );
}
